/**
 * The results screen (UI-002, reworked as SHR-001).
 *
 * Shown when the last ball drops: the result against par (Shark, Runout, Table Run,
 * Chalk Up or Railbird, PRD §4), the score, a mark for every shot, and the ways to
 * share it. The owner's brief (2026-09-25): copy the result, share a link, and a
 * calendar that is not built yet. No replay, and no home page to go back to.
 *
 * Everything shown is what the player's own screen already knows. Nothing here is
 * sent anywhere or trusted by anything: the score that will count is recomputed by
 * the server from the shot log (PRD N3, Phase 4).
 */
import { CALENDAR_COPY, COUNTS, LABELS, PAR_WORDS, RESULT_COPY, SHOT_KEY } from '../config/site.ts';
import {
  copiedResult,
  facebookShareUrl,
  resultLabel,
  shareText,
  xShareUrl,
  type ResultSummary,
} from './share.ts';

/*
 * SHR-003: the marks of X and Facebook (after Simple Icons, CC0), and a link. Markup
 * only, drawn in the button's colour, so the CSP needs nothing and no script of theirs
 * ever loads.
 */
const X_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.6l5.24 6.93zm-1.29 19.5h2.04L6.49 3.24H4.3z"/></svg>';
const FACEBOOK_ICON =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>';
import { createShotKey, shotDot } from './shot-key.ts';

export interface WinScreen {
  /**
   * `link` goes under the result Copy result copies, and into the X and Facebook posts: the bare site, or this exact table in debug mode.
   * `kind`: the attempt that counts, or a practice run after it (DLY-004), which says
   * so and offers no sharing, so a practice score cannot pass for the real one.
   */
  show(result: ResultSummary, penalties: number, link: string, kind: ResultKind): void;
  hide(): void;
  /** Bring the last result back, as it was (UI-012's Result button). */
  reopen(): void;
  /**
   * The day's numbers under a counted result (API-005), once they arrive; empty or
   * absent hides them. Cleared by every `show`, so they cannot follow into practice.
   */
  setDayNumbers(lines: readonly string[]): void;
  destroy(): void;
}

/** How long a "Copied" note stays up. */
const STATUS_MS = 2500;

/** `archive`: a past day from the calendar (DLY-005), which never counts either. */
export type ResultKind = 'counted' | 'practice' | 'archive';

export interface WinScreenActions {
  /**
   * Debug mode only: a player gets one table, as they will get one a day (URL-001), so
   * the button is not even drawn for them.
   */
  readonly onNewTable?: () => void;
  /** Daily tables: play it again for practice, after the attempt that counts (D-029). */
  readonly onPractice?: () => void;
  /** Daily tables: open Statistics (STA-002). */
  readonly onStats?: () => void;
  /** Open the calendar (DLY-005). Without it, the button says "Coming soon". */
  readonly onCalendar?: () => void;
  /**
   * UI-012: the player closed the results (✕, Escape, or a tap outside the card) to
   * knock the cue ball around the cleared table. Without it, the results cannot close.
   */
  readonly onClose?: () => void;
}

export function createWinScreen(host: HTMLElement, actions: WinScreenActions): WinScreen {
  const { onNewTable, onPractice, onStats, onCalendar, onClose } = actions;
  host.innerHTML = `
    <div class="overlay__card result" role="dialog" aria-modal="true" aria-labelledby="win-title">
      ${
        onClose === undefined
          ? ''
          : `<button type="button" class="btn btn--icon result__close" data-close
               aria-label="${RESULT_COPY.close}" title="${RESULT_COPY.close}">&times;</button>`
      }
      <p class="result__kicker" data-kicker>${RESULT_COPY.cleared}</p>
      <h2 class="overlay__title" id="win-title" data-title>${RESULT_COPY.cleared}</h2>
      <p class="overlay__score numeral" data-score>0</p>
      <p class="overlay__detail" data-detail></p>
      <p class="overlay__detail" data-table></p>
      <p class="overlay__detail" data-day hidden></p>
      <ol class="result__shots" aria-label="${RESULT_COPY.shotsLabel}" data-shots></ol>
      <button type="button" class="btn btn--primary result__wide" data-copy>${RESULT_COPY.copy}</button>
      <div class="result__share" data-share>
        <button type="button" class="btn" data-share-x
                aria-label="${RESULT_COPY.shareXLabel}" title="${RESULT_COPY.shareXLabel}">${X_ICON}<span>${RESULT_COPY.shareX}</span></button>
        <button type="button" class="btn" data-share-facebook
                aria-label="${RESULT_COPY.shareFacebookLabel}" title="${RESULT_COPY.shareFacebookLabel}">${FACEBOOK_ICON}<span>${RESULT_COPY.shareFacebook}</span></button>
      </div>
      <div class="result__row">
        ${
          onCalendar === undefined
            ? `<button type="button" class="btn" disabled aria-describedby="result-calendar-soon">
          ${RESULT_COPY.calendar}
          <span class="result__soon" id="result-calendar-soon">${RESULT_COPY.comingSoon}</span>
        </button>`
            : `<button type="button" class="btn" data-calendar>${RESULT_COPY.calendar}</button>`
        }
      </div>
      <p class="result__status" role="status" data-status></p>
      ${
        onStats === undefined
          ? ''
          : `<button type="button" class="btn" data-stats>${RESULT_COPY.statistics}</button>`
      }
      ${
        onPractice === undefined
          ? ''
          : `<button type="button" class="btn" data-practice>${RESULT_COPY.practice}</button>`
      }
      ${
        onNewTable === undefined
          ? ''
          : `<button type="button" class="btn btn--quiet" data-new-table>${RESULT_COPY.newTable}</button>`
      }
    </div>
  `;
  host.hidden = true;

  const kicker = need<HTMLParagraphElement>(host, '[data-kicker]');
  const title = need<HTMLHeadingElement>(host, '[data-title]');
  const score = need<HTMLParagraphElement>(host, '[data-score]');
  const detail = need<HTMLParagraphElement>(host, '[data-detail]');
  const table = need<HTMLParagraphElement>(host, '[data-table]');
  const day = need<HTMLParagraphElement>(host, '[data-day]');
  const shots = need<HTMLOListElement>(host, '[data-shots]');
  // What the colours mean, right under them (owner, 2026-09-25).
  shots.after(createShotKey());
  const copyButton = need<HTMLButtonElement>(host, '[data-copy]');
  const xButton = need<HTMLButtonElement>(host, '[data-share-x]');
  const facebookButton = need<HTMLButtonElement>(host, '[data-share-facebook]');
  const shareButtons = need<HTMLDivElement>(host, '[data-share]');
  const status = need<HTMLParagraphElement>(host, '[data-status]');
  const newTable = host.querySelector<HTMLButtonElement>('[data-new-table]');
  const practiceButton = host.querySelector<HTMLButtonElement>('[data-practice]');
  const statsButton = host.querySelector<HTMLButtonElement>('[data-stats]');
  const calendarButton = host.querySelector<HTMLButtonElement>('[data-calendar]');
  const shareRow = need<HTMLDivElement>(host, '.result__row');
  const closeButton = host.querySelector<HTMLButtonElement>('[data-close]');
  const card = need<HTMLDivElement>(host, '.overlay__card');

  let text = '';
  let link = '';
  let statusTimer: ReturnType<typeof setTimeout> | undefined;

  function say(message: string): void {
    status.textContent = message;
    if (statusTimer !== undefined) clearTimeout(statusTimer);
    statusTimer = setTimeout(() => {
      status.textContent = '';
    }, STATUS_MS);
  }

  async function copy(value: string, done: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      say(done);
      return;
    } catch {
      // Fall through: older browsers, and some embedded ones, refuse the async API.
    }
    // The old way: select a hidden field and copy. Deprecated, but still the only one
    // some browsers allow, and it needs nothing but the click the player just made.
    const field = document.createElement('textarea');
    field.value = value;
    field.setAttribute('readonly', '');
    field.className = 'visually-hidden';
    host.append(field);
    field.select();
    const copied = document.execCommand('copy');
    field.remove();
    // If that failed too, say so rather than pretend: the player would otherwise paste
    // nothing and not know why.
    say(copied ? done : RESULT_COPY.copyFailed);
  }

  // SHR-004: the result and the link together; there is no separate "Copy link".
  const onCopy = (): void => {
    void copy(copiedResult(text, link), RESULT_COPY.copied);
  };
  /** Their own share page, in a new tab that cannot reach back into this one. */
  const openShare = (url: string): void => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const onShareX = (): void => {
    openShare(xShareUrl(text, link));
  };
  const onShareFacebook = (): void => {
    openShare(facebookShareUrl(link));
  };

  const close = (): void => {
    if (onClose === undefined || host.hidden) return;
    host.hidden = true;
    onClose();
  };
  // A tap on the dimmed backdrop, outside the card, closes it too.
  const onBackdrop = (event: MouseEvent): void => {
    if (!card.contains(event.target as Node)) close();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !host.hidden) close();
  };
  closeButton?.addEventListener('click', close);
  if (onClose !== undefined) {
    host.addEventListener('click', onBackdrop);
    document.addEventListener('keydown', onKey);
  }

  copyButton.addEventListener('click', onCopy);
  xButton.addEventListener('click', onShareX);
  facebookButton.addEventListener('click', onShareFacebook);
  if (onNewTable !== undefined) newTable?.addEventListener('click', onNewTable);
  if (onPractice !== undefined) practiceButton?.addEventListener('click', onPractice);
  if (onStats !== undefined) statsButton?.addEventListener('click', onStats);
  if (onCalendar !== undefined) calendarButton?.addEventListener('click', onCalendar);

  return {
    show(result, penalties, shareLink, kind) {
      // Neither a practice run nor an archive day counts, so neither can be shared as a result.
      const practice = kind !== 'counted';
      text = shareText(result);
      link = shareLink;
      const shotCount = result.marks.length;

      title.textContent =
        result.par === undefined ? RESULT_COPY.cleared : resultLabel(result.score, result.par);
      // With a par, the label is the headline and "Table cleared" moves up to a kicker.
      // A practice run always says what it is, where the kicker sits.
      kicker.textContent =
        kind === 'archive'
          ? CALENDAR_COPY.archiveKicker
          : kind === 'practice'
            ? RESULT_COPY.practiceKicker
            : RESULT_COPY.cleared;
      kicker.hidden = !practice && result.par === undefined;
      copyButton.hidden = practice;
      // Sharing is for the attempt that counts; Calendar, on its own row, stays reachable.
      shareButtons.hidden = practice;
      shareRow.hidden = practice && onCalendar === undefined;
      if (practiceButton !== null) {
        practiceButton.textContent =
          kind === 'counted'
            ? RESULT_COPY.practice
            : kind === 'archive'
              ? RESULT_COPY.playAgain
              : RESULT_COPY.practiceAgain;
      }
      score.textContent = String(result.score);
      detail.textContent =
        result.par === undefined
          ? describeRun(shotCount, penalties)
          : `${describeRun(shotCount, penalties)} · ${describePar(result.score, result.par)}`;
      table.textContent = `${
        result.puzzleNumber === undefined ? '' : `#${String(result.puzzleNumber)} · `
      }${String(result.sizeFt)}ft · ${String(result.ballCount)} ${
        result.ballCount === 1 ? COUNTS.ball[0] : COUNTS.ball[1]
      }`;

      shots.replaceChildren(
        ...result.marks.map((mark, i) => {
          const item = document.createElement('li');
          item.className = 'result__shot';
          // UI-022: its place in the order the dots appear (game.css).
          item.style.setProperty('--i', String(i));
          // Read out in words; the colour alone would say nothing to a screen reader.
          item.setAttribute(
            'aria-label',
            `${RESULT_COPY.shot} ${String(i + 1)}: ${SHOT_KEY[mark]}`,
          );
          item.title = SHOT_KEY[mark];
          item.append(shotDot(mark));
          return item;
        }),
      );
      status.textContent = '';
      day.replaceChildren();
      day.hidden = true;
      host.hidden = false;
      (practice ? (practiceButton ?? copyButton) : copyButton).focus();
    },

    hide() {
      host.hidden = true;
    },

    reopen() {
      host.hidden = false;
      (closeButton ?? copyButton).focus();
    },

    setDayNumbers(lines) {
      // One line each; text only, never markup.
      const nodes: (Node | string)[] = [];
      for (const [i, line] of lines.entries()) {
        if (i > 0) nodes.push(document.createElement('br'));
        nodes.push(line);
      }
      day.replaceChildren(...nodes);
      day.hidden = lines.length === 0;
    },

    destroy() {
      closeButton?.removeEventListener('click', close);
      host.removeEventListener('click', onBackdrop);
      document.removeEventListener('keydown', onKey);
      copyButton.removeEventListener('click', onCopy);
      xButton.removeEventListener('click', onShareX);
      facebookButton.removeEventListener('click', onShareFacebook);
      if (onNewTable !== undefined) newTable?.removeEventListener('click', onNewTable);
      if (onPractice !== undefined) practiceButton?.removeEventListener('click', onPractice);
      if (onStats !== undefined) statsButton?.removeEventListener('click', onStats);
      if (onCalendar !== undefined) calendarButton?.removeEventListener('click', onCalendar);
      if (statusTimer !== undefined) clearTimeout(statusTimer);
    },
  };
}

/**
 * "6 shots · 1 penalty" — the line under the score.
 *
 * Exported so it can be tested without a DOM. It was worth extracting: the version
 * that pluralised by appending an "s" produced "2 penaltys", and only a browser
 * caught it.
 */
export function describeRun(shots: number, penalties: number): string {
  const taken = `${String(shots)} ${count(shots, COUNTS.shot)}`;
  if (penalties === 0) return taken;
  return `${taken} · ${String(penalties)} ${count(penalties, COUNTS.penalty)}`;
}

// The label lives with the share string, which needs it too; re-exported for callers.
export { resultLabel };

/** "Par 7 · 1 over", "Par 7 · level par", "Par 7 · 2 under". */
export function describePar(score: number, par: number): string {
  const delta = score - par;
  const where =
    delta === 0
      ? PAR_WORDS.level
      : `${String(Math.abs(delta))} ${delta > 0 ? PAR_WORDS.over : PAR_WORDS.under}`;
  return `${LABELS.par} ${String(par)} · ${where}`;
}

function count(value: number, forms: readonly [string, string]): string {
  return value === 1 ? forms[0] : forms[1];
}

function need<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (found === null) throw new Error(`win screen: ${selector} is missing`);
  return found;
}
